// Carries the page URL where a signup was initiated so the double opt-in row
// records the originating page (EA-824), matching the newsletter form's
// pageUrl. Set client-side right before auth initiation; read server-side when
// the new user is created. Client-set (not httpOnly) + SameSite=Lax so it
// survives the Google OAuth round-trip and reaches the signup request.
export const SIGNUP_URL_COOKIE = 'eff-signup-url'
export const SIGNUP_OPT_IN_SOURCE_COOKIE = 'eff-signup-opt-in-source'

export type AccountOptInSource = 'account' | 'deal_claim'

// Client-only: stamp the current page as the signup source. No-op on the server.
export function markSignupSource(optInSource: AccountOptInSource = 'account'): void {
  if (typeof document === 'undefined') return
  try {
    // Store only origin + pathname — never the query string or hash, which can
    // carry sensitive params and would otherwise ride along in every Cookie
    // header. The server normalizes to the same shape on read.
    const value = encodeURIComponent(window.location.origin + window.location.pathname)
    document.cookie = `${SIGNUP_URL_COOKIE}=${value}; path=/; max-age=600; samesite=lax`
    document.cookie = `${SIGNUP_OPT_IN_SOURCE_COOKIE}=${optInSource}; path=/; max-age=600; samesite=lax`
  } catch {
    // Cookies unavailable — the server simply falls back to a default page URL.
  }
}

// Server: reduce a raw signup-source URL to a safe origin + pathname (drops the
// query string + hash so no sensitive params are stored). Returns null if the
// value is missing or unparseable.
export function normalizeSignupSourceUrl(raw: string | undefined | null): string | null {
  if (!raw) return null
  try {
    const url = new URL(decodeURIComponent(raw))
    return url.origin + url.pathname
  } catch {
    return null
  }
}

export function normalizeSignupOptInSource(raw: string | undefined | null): AccountOptInSource {
  return raw === 'deal_claim' ? 'deal_claim' : 'account'
}

export function getAccountOptInSourceForRedirect(
  redirect: string | null | undefined,
): AccountOptInSource {
  if (!redirect) return 'account'

  try {
    const url = new URL(redirect, 'https://efficient.app')
    return url.pathname.startsWith('/deals/') && url.searchParams.get('claim') === 'true'
      ? 'deal_claim'
      : 'account'
  } catch {
    return 'account'
  }
}
